import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import {
  channelAccessFilter,
  workspaceFullMemberGuard,
  workspaceAdminSurfaceGuard,
  workspaceMemberRowVisibility,
} from "./access-filters";

const byUser = defineQuery(({ ctx: { sub } }) =>
  // Soft-delete: must filter `removed_at IS NULL` on the caller's
  // membership. Without it, a kicked user keeps seeing the workspace
  // in their sidebar until they reload — the soft-deleted
  // `workspace_members` row (kept so historical message senders still
  // resolve via `userAccessFilter`) satisfies `user_id = sub` and
  // makes the workspace show up. Symptom: kicked user sees the
  // workspace tile + empty channels list for ages until the
  // `MembershipLost` dialog finally fires. See
  // `.claude/rules/queries.md` — "Soft-delete: always filter
  // `removed_at IS NULL` on the caller's membership".
  zql.workspaces.whereExists("members", (q) =>
    q.where("user_id", sub).where("removed_at", "IS", null),
  ),
);

// Phase 4 — workspace fan-out composite. Single subscription returning the
// workspace + members + channels + coworkers + workspace_skills. Replaces
// the 5-way fan-out in `useTeamMemberAndAgentData`.
//
// Per-relationship visibility — four different rules in one composite:
//
//   * `members`: 3-branch rule via `workspaceMemberRowVisibility`. Full
//     members see all rows; collaborators see only their own row + members
//     who share a channel with them in this workspace. SAME helper used by
//     `workspace_members.byWorkspace` so the per-table query and the
//     composite's `.related("members")` can't drift.
//   * `channels`: caller must be a direct, active member of the channel
//     (via `channelAccessFilter`). SAME helper used by
//     `channels.byWorkspace` — without this, private channels would leak
//     to anyone in the workspace.
//   * `coworkers` + `workspace_skills`: FULL_MEMBER_ROLES only via
//     `workspaceFullMemberGuard` re-traversed through the workspace
//     relationship.
//
// Pattern note: this is the canonical "parent visible to all members, some
// children admin-gated, others use per-row visibility" shape for Ano's
// per-(user, workspace) role model. Zero docs §"Permission Patterns" only
// cover global-role JS branching; zbugs uses a row-level `visibility`
// column. Ano's pattern is closure-captured `sub` + nested `whereExists` +
// reusable access-filter helpers — same primitives the existing per-table
// queries already use, just nested inside `.related()` and consolidated
// onto helpers so behavior can't drift between query identities.
const byIdComposite = defineQuery(
  z.string(),
  ({ args: workspaceId, ctx: { sub } }) =>
    zql.workspaces
      .where("id", workspaceId)
      .whereExists("members", (mq) =>
        mq.where("user_id", sub).where("removed_at", "IS", null),
      )
      .related("members", (q) =>
        // Filter soft-deleted (kicked) rows so the composite mirrors
        // the live roster. `workspaceMemberRowVisibility` decides
        // visibility per role (full member sees all, collaborator
        // sees own + same-channel members) but does NOT filter
        // `removed_at` on the target row, so without this filter
        // every consumer of `composite.members` (TeamView, MessageArea
        // composite, mention picker, etc.) keeps showing the kicked
        // user post-removal.
        q
          .where("removed_at", "IS", null)
          .where(workspaceMemberRowVisibility(sub, workspaceId))
          .related("user"),
      )
      .related("channels", (q) => q.where(channelAccessFilter(sub)))
      .related("coworkers", (q) =>
        q
          .whereExists("workspace", workspaceFullMemberGuard(sub))
          .related("user"),
      )
      .related("workspace_skills", (q) =>
        q
          .where("enabled", true)
          // Admin-only in a PUBLIC workspace: skills (prompts + tool bindings)
          // are workspace management. No member-facing surface reads composite
          // skills (the slash-command menu uses the gated REST list); they're
          // only rendered by the admin-gated Skills/Team views, so dropping the
          // replication for public-ws non-admins is non-breaking.
          .whereExists("workspace", workspaceAdminSurfaceGuard(sub)),
      )
      .one(),
);

export const workspacesQueries = {
  /** @deprecated Use `byUser` — identical since ctx-based auth migration */
  all: byUser,
  byUser,
  byIdComposite,
};
